<template>
  <button
    data-test-green-card-download
    class="button large secondary"
    :disabled="isDisabled"
    @click="onButtonClick"
    v-html="buttonText"
  />
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { TextAlignment } from 'pdf-lib';
import PdfFormService from '@/common/pdfFormService';
import RequestService from '@/common/requestService';
import { GreenCardInformation, GreenCardInformationData, ValidDate } from './types/GreenCardDownload';
import Utility from '@/common/UtilityFunctions.js';

export default defineComponent({
  name: 'GreenCardDownloadButton',
  props: {
    policyNumber: {
      type: String,
      required: true
    },
    buttonText: {
      type: String,
      default: () => ''
    }
  },
  data(): GreenCardInformationData {
    return {
      information: null,
      pdfPath: '/dist/static/portal/files/green_card_form.pdf',
      isDisabled: false,
      validFrom: new Date()
    };
  },
  created() {
    this.getGreenCardInformation(this.policyNumber.replace('/', '-'));
  },
  methods: {
    async getGreenCardInformation(encodedPolicyNumber: string): Promise<void> {
      RequestService.get<GreenCardInformation>(
        Utility.constants().API.green_card_for_pdf_get + encodedPolicyNumber
      )
        .then((response) => {
          this.information = response.data;
        })
        .catch((err) => Utility.logError(err, this));
    },
    dispatchDownloadTrackingEvent(): void {
      this.$store.dispatch('Events/register', {
        event: 'green-card-download',
        id: 'greenCard'
      });
    },
    onButtonClick(): void {
      this.isDisabled = true;
      this.fillPdf()
        .then(() => (this.isDisabled = false))
        .catch(() => (this.isDisabled = false));
      this.dispatchDownloadTrackingEvent();
    },
    getValidDate(date: Date): ValidDate {
      return {
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear()
      };
    },
    async fillPdf(): Promise<void> {
      const domain = Utility.domain();
      const pdfDocument = await PdfFormService.loadPdfDocument(domain + this.pdfPath);
      const information = this.information;
      if (!information) return;

      const form = pdfDocument.getForm();
      const validFrom = this.getValidDate(this.validFrom);
      const validTo = this.getValidDate(new Date(information.expirationDate));

      PdfFormService.setReadOnlyTextField(form, 'validFromDay', `${validFrom.day}`);
      PdfFormService.setReadOnlyTextField(form, 'validFromMonth', `${validFrom.month}`);
      PdfFormService.setReadOnlyTextField(form, 'validFromYear', `${validFrom.year}`);
      PdfFormService.setReadOnlyTextField(form, 'validToDay', `${validTo.day}`);
      PdfFormService.setReadOnlyTextField(form, 'validToMonth', `${validTo.month}`);
      PdfFormService.setReadOnlyTextField(form, 'validToYear', `${validTo.year}`);

      PdfFormService.setReadOnlyTextField(
        form,
        'registrationNumber',
        information.registrationNumber,
        TextAlignment.Left
      );
      PdfFormService.setReadOnlyTextField(form, 'countryCode', information.brandCode.toString());
      PdfFormService.setReadOnlyTextField(
        form,
        'serialCode',
        information.policyNumber,
        TextAlignment.Left
      );
      PdfFormService.setReadOnlyTextField(form, 'category', information.categoryOfVehicle);
      PdfFormService.setReadOnlyTextField(
        form,
        'make',
        information.makeOfVehicle,
        TextAlignment.Left
      );

      PdfFormService.setReadOnlyTextField(form, 'name', information.name, TextAlignment.Left);
      PdfFormService.setReadOnlyTextField(
        form,
        'addressLineOne',
        information.address[0],
        TextAlignment.Left
      );
      PdfFormService.setReadOnlyTextField(
        form,
        'addressLineTwo',
        information.address[1],
        TextAlignment.Left
      );

      await PdfFormService.downloadPdfDocument(pdfDocument, 'groent-kort.pdf');
    }
  }
});
</script>
